import { Injectable } from '@angular/core'
import { JsaForm } from '../models/jsa.model'
import { JsaFormActions } from '../actions/jsa_form.actions'
import { DateTime } from 'luxon';
import { Select, State, Action, StateContext, Selector, Store, NgxsOnInit, NgxsOnChanges, NgxsSimpleChange } from '@ngxs/store'
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { StorageService } from 'src/app/services/storage.service'
import { JsaService } from 'src/app/services/jsa.service'
import { Observable } from 'rxjs';
import { UserState } from 'src/app/state-man/state/user.state';
import { User } from 'src/app/state-man/models/user.model';
import { ConnectionStatusEnum, NetworkService } from 'src/app/services/network.service';
import { OfflineService } from 'src/app/services/offline.service';

export class JsaFormStateModel {
    is_refreshing: boolean
    pending_count: number
    last_update: string
    jsa_forms: JsaForm[]
}

@State<JsaFormStateModel>({
    name: 'jsa_form',
    defaults: {
        is_refreshing: true,
        pending_count: 0,
        last_update: '',
        jsa_forms: []
    }
})
@Injectable()
export class JsaFormState implements NgxsOnInit, NgxsOnChanges {
    @Select(UserState.getUser) user$: Observable<User>

    constructor(
        private storageService: StorageService,
        private store: Store,
        private jsaService: JsaService,
        private networkService: NetworkService,
        private offline: OfflineService
    ) {}

    async ngxsOnInit(ctx: StateContext<JsaFormStateModel>) {           
        this.user$.subscribe(async (user: User) => {            
            if (user.is_logged_in) {     
                this.storageService.events.subscribe(async (status: any) => {
                    if (this.storageService.is_initialized) {                                                                                            
                        await this.reloadStateFromStorage()
                        this.store.dispatch(new JsaFormActions.IncrementPendingCount())
                    }
                })                           
            }
        }) 
    }

    async ngxsOnChanges(change: NgxsSimpleChange) {        
        if (this.storageService.is_initialized) {
            const state_model: JsaFormStateModel = change.currentValue 
            this.storageService.set('jsa_forms', JSON.stringify(state_model))    
        }  
      
        if (this.networkService.currentStatus === ConnectionStatusEnum.Online) {                
            if (change.previousValue && change.previousValue.pending_count < change.currentValue.pending_count) {
                const state_model: JsaFormStateModel = change.currentValue 
                await this.offline.processPendingJsaForms(state_model.jsa_forms) 
            }                                
        }        
    }

    async reloadStateFromStorage() {   
        const json = await this.storageService.get('jsa_forms');        
        if (json) {
            const state_model: JsaFormStateModel = JSON.parse(json)
            if (state_model) {                                                               
                this.store.dispatch(new JsaFormActions.Init(state_model));
                return
            }
        }
        this.store.dispatch(new JsaFormActions.Refresh())
    }

    async reloadStateFromApi() : Promise<boolean> {
        const jsaFormObjs = await this.jsaService.getJsaForms()
        if (jsaFormObjs != null) {
          const jsaForms = JsaForm.buildMany(jsaFormObjs)
          this.store.dispatch(new JsaFormActions.Merge(jsaForms))
          return true
        } 
        return false        
    }

    @Selector()
    static getLastUpdate(state: JsaFormStateModel) {
        return state.last_update
    }

    @Selector()
    static getIsRefreshing(state: JsaFormStateModel) {
        return state.is_refreshing
    }

    @Selector()
    static getState(state: JsaFormStateModel) {
        return state
    }

    @Selector()
    static getJsaForms(state: JsaFormStateModel) {
        return state.jsa_forms
    }

    @Selector()
    static getJsaForm(state: JsaFormStateModel) {
        return (id: number) => {
            return state.jsa_forms.filter(s => s.id === id)
        }
    }

    @Action(JsaFormActions.Refresh)
    async refresh({patchState}: StateContext<JsaFormStateModel>, {  }:JsaFormActions.Refresh) {                  
        patchState({is_refreshing: true})
        if (!await this.reloadStateFromApi()) {            
            patchState({is_refreshing: false})
        }
    }

    @Action(JsaFormActions.IncrementPendingCount)
    incrementPendingCount({patchState, getState}: StateContext<JsaFormStateModel>, {  }:JsaFormActions.IncrementPendingCount) {     
        const state = getState()
        patchState({
            pending_count: state.pending_count + 1
        })
    }

    @Action(JsaFormActions.AddOne)
    addOne({getState, patchState}: StateContext<JsaFormStateModel>, { payload }:JsaFormActions.AddOne) {
        const state = getState()        
        patchState({
            jsa_forms: [...state.jsa_forms, payload]
        })
    }

    @Action(JsaFormActions.AddMany)
    addMany({getState, patchState}: StateContext<JsaFormStateModel>, { payload }:JsaFormActions.AddMany) {
        const state = getState()        
        patchState({
            jsa_forms: [...state.jsa_forms].concat(payload)
        })
    }

    @Action(JsaFormActions.UpdateOne)
    updateOne({setState}: StateContext<JsaFormStateModel>, { payload }:JsaFormActions.UpdateOne) {
        setState(
            patch<JsaFormStateModel>({
                jsa_forms: updateItem((item: JsaForm) => item.id === payload.id, 
                    patch<JsaForm>
                        (payload)
                    ),
                last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")
            })
        )
    }

    @Action(JsaFormActions.ApiAddedOne)
    apiAddedOne({ setState }: StateContext<JsaFormStateModel>, { payload }:JsaFormActions.ApiAddedOne) {         
        setState(
            patch<JsaFormStateModel>
                ({
                    jsa_forms: updateItem((item: JsaForm) => item.uuid === payload.uuid, 
                        patch<JsaForm>
                            ({
                                id: payload.id,
                                is_pending_save: false
                            })
                        ),
                    last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")                 
                })                
        )
    }

    @Action(JsaFormActions.ApiUpdatedOne)
    apiUpdatedOne({ setState }: StateContext<JsaFormStateModel>, { payload }:JsaFormActions.ApiUpdatedOne) {         
        setState(
            patch<JsaFormStateModel>
                ({
                    jsa_forms: updateItem((item: JsaForm) => item.id === payload.id, 
                        patch<JsaForm>
                            ({
                                is_pending_save: false
                            })
                        ),
                    last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")                 
                })                
        )
    }

    @Action(JsaFormActions.DeleteOne)
    deleteOne({setState}: StateContext<JsaFormStateModel>, { payload }:JsaFormActions.DeleteOne) {
        setState(
            patch<JsaFormStateModel>
                ({
                    jsa_forms: updateItem((item: JsaForm) => item.id === payload.id, 
                        patch<JsaForm>
                            (payload)
                        ),
                    last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")                 
                })                
        )
    }

    @Action(JsaFormActions.ApiDeletedOne)
    apiDeletedOne({ setState }: StateContext<JsaFormStateModel>, { payload }:JsaFormActions.ApiDeletedOne) {         
        setState(
            patch<JsaFormStateModel>
                ({
                    jsa_forms: removeItem((item: JsaForm) => item.id === payload.id),
                    last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a")                 
                })                
        )
    }

    @Action(JsaFormActions.Init)
    init({setState, getState}: StateContext<JsaFormStateModel>, { payload }:JsaFormActions.Init) {     
        const state = getState()
        payload.is_refreshing = state.is_refreshing        
        setState(payload)
        this.store.dispatch(new JsaFormActions.Refresh())
    }

    @Action(JsaFormActions.Set)
    set({setState, getState}: StateContext<JsaFormStateModel>, { payload }:JsaFormActions.Set) {  
        const state = getState()              
        setState({
            is_refreshing: state.is_refreshing,
            pending_count: 0,
            last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a"),
            jsa_forms: payload
        })
    }

    @Action(JsaFormActions.Merge)
    merge({getState, setState}: StateContext<JsaFormStateModel>, { payload }:JsaFormActions.Merge) {   
        // This is to merge data from the API provided in payload, into the app state.
        // Basically, if we have server data, we trust that, so we want to replace the 
        // current app state with the payload, except:
        // 1. Any images pending upload or delete need to be merged in, even if the JSA no longer
        //    exists.
        const state = getState()        
        const oldJsaForms = [...state.jsa_forms]
        let newJsaForms = [...payload]

        // For each jsa form in state now...
        for (const jsaForm of oldJsaForms) {   
            // If jsa form id is not in the list from the server       
            let newJsaForm = newJsaForms.find(f => f.id === jsaForm.id)   
            if (!newJsaForm) {                
                // If there are any pending save/delete, keep the entire object in state, else do not since no longer on server                
                if (jsaForm.is_pending_save || jsaForm.is_pending_delete) {                    
                    newJsaForms.push(jsaForm)
                }
            } else {                
                // Object in state is in the payload from the server. 
                // 1. If anything is pending, we need to preserve the one in state, first removing the one that is from the server
                // 1. Else we leave the one from the server to set in the new state
                if (jsaForm.is_pending_save || jsaForm.is_pending_delete) {
                    // Remove one that is from the server
                    newJsaForms = newJsaForms.filter((obj) => {return obj !== newJsaForm});                        
                    // Add the one from state back in
                    newJsaForms.push(jsaForm)
                } else if(jsaForm.uuid) {
                    newJsaForm.uuid = jsaForm.uuid
                }
            }
        }      

        setState({
            is_refreshing: false,
            pending_count: 0,
            last_update: DateTime.now().toFormat("M/d/yyyy, h:mm a"),
            jsa_forms: newJsaForms
        })
    }

}
